import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { closeMainModal, openMainModal } from '../../../../store/actions/profileActions';
import {
  formatSheetRowPriceInputValue,
  formatSheetRowPromotionDiscountInputValue,
  parseSheetPriceInputToNumber,
  parseSheetPromotionDiscountInputToNumber,
  type ManualMaterialSubmitPayload,
  type SheetRowFieldPatch,
} from '../../../../services/materialCommerceSheetService.ts';
import { useDebouncedCatalogMaterialSearch } from '../../../../hooks/useDebouncedCatalogMaterialSearch.ts';
import {
  fetchCatalogCategories,
  searchItemsToCatalogPicks,
} from '../../../../services/catalogMaterialsService.ts';
import {
  collectManualAddMaterialClientFieldErrors,
  isManualAddFormClientValid,
  mergeManualAddMaterialClientFieldErrorsIntoState,
  type ManualAddMaterialFieldErrors,
} from '../../../Comercios/CommerceSheetReviewStep/commerceManualAddMaterialValidation.ts';
import { CommerceSheetManualAddMaterialModal } from '../../../Comercios/CommerceSheetReviewStep/CommerceSheetManualAddMaterialModal.tsx';
import {
  type CommerceMaterialsViewMode,
  type FlatMaterialRow,
  filterFlatRows,
} from './commerceMaterialsModel.ts';
import {
  type CatalogPickOption,
} from './commerceMaterialsCatalogPick.ts';
import { CommerceMaterialsCatalogEditModal } from './CommerceMaterialsCatalogEditModal.tsx';
import { useHorizontalDragScroll } from '../../../../hooks/onboardingV2/useHorizontalDragScroll.ts';
import styles from './CommerceMaterialsCatalog.module.scss';

const PAGE_SIZE = 40;
const CATALOG_TABLE_SCROLL_OFFSET_PX = 80;

function formatMoneyArs(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return String(Math.round(value));
  }
}

function promotionLabel(mc: FlatMaterialRow['material']['commerceRow']): string {
  if (!mc) return '—';
  const parts: string[] = [];
  if (mc.promotion && mc.promotion.trim()) parts.push(mc.promotion.trim());
  if (mc.promotion_discount != null && Number.isFinite(mc.promotion_discount)) {
    parts.push(`Dto. ${mc.promotion_discount}%`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function quoteFlag(m: FlatMaterialRow['material']): boolean {
  return m.catalogUnquoted || Boolean(m.commerceRow?.unquoted);
}

const EDIT_SAVE_ERROR_TOAST =
  'No pudimos guardar los cambios. Revisá tu conexión o probá de nuevo en unos instantes.';

interface CommerceMaterialsCatalogProps {
  mode: CommerceMaterialsViewMode;
  flatRows: FlatMaterialRow[];
  onPublicMaterialDetailClick?: (materialId: number | string) => void;
  disableActions?: boolean;
  onEditItem?: (itemId: number, patch: {
    price: number | null;
    unquoted: boolean;
    valueAddedTax: boolean;
    promotion: string;
    promotionDiscount: number | null;
  }) => Promise<void>;
  onDeleteItem?: (itemId: number) => Promise<void>;
  onAddItem?: (payload: ManualMaterialSubmitPayload) => Promise<{ ok: boolean; message?: string }>;
  fileImportHref?: string;
}

const CommerceMaterialsCatalog: React.FC<CommerceMaterialsCatalogProps> = ({
  mode,
  flatRows,
  onPublicMaterialDetailClick,
  disableActions = false,
  onEditItem,
  onDeleteItem,
  onAddItem,
  fileImportHref,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [filterQ, setFilterQ] = useState('');
  const [categoryId, setCategoryId] = useState<string | 'all'>('all');
  const [page, setPage] = useState(1);
  const catalogTableDragScroll = useHorizontalDragScroll([], { wheel: false });
  const [editingRow, setEditingRow] = useState<FlatMaterialRow | null>(null);
  const [editItemId, setEditItemId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addModalSubmitting, setAddModalSubmitting] = useState(false);
  const [addModalError, setAddModalError] = useState('');
  const [modCategoryId, setModCategoryId] = useState('');
  const [modRawName, setModRawName] = useState('');
  const [modDesc, setModDesc] = useState('');
  const [modPromo, setModPromo] = useState('');
  const [modDisc, setModDisc] = useState('');
  const [modPrice, setModPrice] = useState('');
  const [modUnit, setModUnit] = useState('');
  const [modUnquoted, setModUnquoted] = useState(true);
  const [modVat, setModVat] = useState(false);
  const [addModalCategoryOptions, setAddModalCategoryOptions] = useState<Array<{ value: string; label: string }>>(
    []
  );
  const [catalogMaterialPick, setCatalogMaterialPick] = useState<CatalogPickOption | null>(null);
  const [addModalFieldErrors, setAddModalFieldErrors] = useState<ManualAddMaterialFieldErrors>({});
  const [addModalValidationAttempted, setAddModalValidationAttempted] = useState(false);
  const [feedbackToast, setFeedbackToast] = useState<{ variant: 'success' | 'error'; text: string } | null>(
    null
  );

  const categoryOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of flatRows) {
      m.set(r.categoryId, r.categoryName);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [flatRows]);

  const filtered = useMemo(
    () => filterFlatRows(flatRows, { q: filterQ, categoryId }),
    [flatRows, filterQ, categoryId]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [filterQ, categoryId, flatRows.length]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    if (!feedbackToast) return undefined;
    const id = window.setTimeout(() => setFeedbackToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [feedbackToast]);

  const editItemWithFeedback = useCallback<
    CommerceMaterialsCatalogProps['onEditItem']
  >(
    async (itemId, patch) => {
      if (!onEditItem) return;
      try {
        await onEditItem(itemId, patch);
        setFeedbackToast({ variant: 'success', text: 'Cambios guardados.' });
      } catch (e: unknown) {
        setFeedbackToast({ variant: 'error', text: EDIT_SAVE_ERROR_TOAST });
        throw e;
      }
    },
    [onEditItem]
  );

  const scrollCatalogTableIntoView = useCallback(() => {
    window.setTimeout(() => {
      const el = catalogTableDragScroll.ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - CATALOG_TABLE_SCROLL_OFFSET_PX;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 0);
  }, [catalogTableDragScroll.ref]);

  const onCatalogPageNav = useCallback(
    (dir: 'prev' | 'next') => {
      setPage((p) => {
        if (dir === 'prev') return Math.max(1, p - 1);
        return Math.min(pageCount, p + 1);
      });
      scrollCatalogTableIntoView();
    },
    [pageCount, scrollCatalogTableIntoView]
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const fromN = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toN = Math.min(filtered.length, page * PAGE_SIZE);
  const ownerActionsEnabled = mode === 'owner' && onEditItem != null && onDeleteItem != null;
  const ownerManualAddEnabled = mode === 'owner' && onAddItem != null;
  const ownerCatalogToolbarEnabled =
    mode === 'owner' && (onAddItem != null || Boolean(fileImportHref?.trim()));

  const listedMaterialIds = useMemo(
    () =>
      flatRows
        .map((r) => Number(r.material.id))
        .filter((n) => Number.isFinite(n) && n > 0),
    [flatRows]
  );

  const {
    items: catalogSearchItems,
    status: catalogSearchStatus,
    error: catalogSearchError,
  } = useDebouncedCatalogMaterialSearch(modRawName, {
    enabled: addOpen && !catalogMaterialPick,
    limit: 40,
    excludeIds: listedMaterialIds,
    categoryId: modCategoryId || undefined,
  });

  const globalCatalogMatches = useMemo(() => {
    if (catalogMaterialPick || catalogSearchStatus !== 'ready') return [];
    if (modRawName.trim().length < 2) return [];
    return searchItemsToCatalogPicks(catalogSearchItems);
  }, [catalogMaterialPick, catalogSearchStatus, modRawName, catalogSearchItems]);

  const globalCatalogStatus =
    catalogMaterialPick || modRawName.trim().length < 2
      ? 'idle'
      : catalogSearchStatus === 'loading'
        ? 'loading'
        : catalogSearchStatus === 'error'
          ? 'error'
          : 'ready';

  const globalCatalogFetchError = catalogSearchError;

  const onModCategoryChange = useCallback((v: string) => {
    setModCategoryId(v);
    setCatalogMaterialPick((prev) => {
      if (!prev) return null;
      return String(prev.categoryId) === v ? prev : null;
    });
  }, []);

  const resetAddModalForm = useCallback(() => {
    setModCategoryId('');
    setModRawName('');
    setModDesc('');
    setModPromo('');
    setModDisc('');
    setModPrice('');
    setModUnit('');
    setModUnquoted(true);
    setModVat(false);
    setAddModalSubmitting(false);
    setAddModalError('');
    setCatalogMaterialPick(null);
    setAddModalFieldErrors({});
    setAddModalValidationAttempted(false);
  }, []);

  const manualAddSaveDisabled = useMemo(() => {
    if (addModalSubmitting) return true;
    if (!addModalValidationAttempted) return false;
    if (
      !isManualAddFormClientValid(modRawName, modCategoryId, modUnit, catalogMaterialPick, modUnquoted, modPrice)
    ) {
      return true;
    }
    return Object.values(addModalFieldErrors).some((v) => Boolean(v?.trim()));
  }, [
    addModalSubmitting,
    addModalValidationAttempted,
    modRawName,
    modCategoryId,
    modUnit,
    catalogMaterialPick,
    modUnquoted,
    modPrice,
    addModalFieldErrors,
  ]);

  useEffect(() => {
    if (!addModalValidationAttempted) return;
    setAddModalFieldErrors((prev) =>
      mergeManualAddMaterialClientFieldErrorsIntoState(
        prev,
        modRawName,
        modCategoryId,
        modUnit,
        catalogMaterialPick,
        modUnquoted,
        modPrice
      )
    );
  }, [
    modRawName,
    modCategoryId,
    modUnit,
    catalogMaterialPick,
    modUnquoted,
    modPrice,
    addModalValidationAttempted,
  ]);

  useEffect(() => {
    setAddModalFieldErrors((p) => {
      if (!p.price) return p;
      const n = { ...p };
      delete n.price;
      return n;
    });
  }, [modPrice]);

  useEffect(() => {
    setAddModalFieldErrors((p) => {
      if (!p.discount) return p;
      const n = { ...p };
      delete n.discount;
      return n;
    });
  }, [modDisc]);

  const submitAddMaterialModal = useCallback(() => {
    if (!onAddItem) return;
    const name = modRawName.trim();
    const clientErrs = collectManualAddMaterialClientFieldErrors(
      modRawName,
      modCategoryId,
      modUnit,
      catalogMaterialPick,
      modUnquoted,
      modPrice
    );
    if (Object.keys(clientErrs).length > 0) {
      setAddModalFieldErrors(clientErrs);
      setAddModalValidationAttempted(true);
      setAddModalError('');
      return;
    }
    setAddModalFieldErrors({});
    setAddModalValidationAttempted(false);
    setAddModalError('');
    setAddModalSubmitting(true);
    void (async () => {
      try {
        if (catalogMaterialPick) {
          const materialId = Number(catalogMaterialPick.materialId);
          const userCategoryId = Number(catalogMaterialPick.categoryId);
          if (
            !Number.isFinite(materialId) ||
            materialId <= 0 ||
            !Number.isFinite(userCategoryId) ||
            userCategoryId <= 0
          ) {
            setAddModalError('Elegí de nuevo un material de la lista o completá los datos a mano.');
            return;
          }
          const effectiveUnquoted = modUnquoted || !modPrice.trim();
          let price: number | null = null;
          if (!effectiveUnquoted && modPrice.trim()) {
            const pn = parseSheetPriceInputToNumber(modPrice.trim());
            if (pn == null) {
              setAddModalFieldErrors((p) => ({ ...p, price: 'Revisá el formato del precio.' }));
              setAddModalValidationAttempted(true);
              setAddModalError('');
              return;
            }
            price = pn;
          }
          let promotionDiscount: number | null = null;
          if (modDisc.trim()) {
            const dn = parseSheetPromotionDiscountInputToNumber(modDisc.trim());
            if (dn == null) {
              setAddModalFieldErrors((p) => ({ ...p, discount: 'Revisá el formato del descuento.' }));
              setAddModalValidationAttempted(true);
              setAddModalError('');
              return;
            }
            promotionDiscount = dn;
          }
          const res = await onAddItem({
            kind: 'catalog',
            input: {
              materialId,
              userCategoryId,
              rawName: catalogMaterialPick.materialName.trim() || '—',
              price,
              unquoted: effectiveUnquoted,
              valueAddedTax: effectiveUnquoted ? false : modVat,
              promotion: modPromo.trim() || null,
              promotionDiscount,
            },
          });
          if (!res.ok) {
            setAddModalError((res.message ?? '').trim() || 'No se pudo registrar el material.');
            return;
          }
          setFeedbackToast({ variant: 'success', text: 'Material guardado en tu catálogo.' });
          setAddOpen(false);
          resetAddModalForm();
          return;
        }
        const cid = Number(modCategoryId);
        const effectiveUnquoted = modUnquoted || !modPrice.trim();
        const discRaw = modDisc.trim();
        if (discRaw) {
          const dn = parseSheetPromotionDiscountInputToNumber(discRaw);
          if (dn == null) {
            setAddModalFieldErrors((p) => ({ ...p, discount: 'Revisá el formato del descuento.' }));
            setAddModalValidationAttempted(true);
            setAddModalError('');
            return;
          }
        }
        if (!effectiveUnquoted) {
          const priceRaw = modPrice.trim();
          if (priceRaw) {
            const pn = parseSheetPriceInputToNumber(priceRaw);
            if (pn == null) {
              setAddModalFieldErrors((p) => ({ ...p, price: 'Revisá el formato del precio.' }));
              setAddModalValidationAttempted(true);
              setAddModalError('');
              return;
            }
          }
        }
        const initialPatch: Partial<SheetRowFieldPatch> = {};
        if (modDesc.trim()) initialPatch.description = modDesc.trim();
        if (modPromo.trim()) initialPatch.promotion = modPromo.trim();
        if (discRaw) {
          const dn = parseSheetPromotionDiscountInputToNumber(discRaw);
          if (dn != null) initialPatch.promotionDiscount = formatSheetRowPromotionDiscountInputValue(dn);
        }
        if (!effectiveUnquoted) {
          const priceRaw = modPrice.trim();
          if (priceRaw) {
            const pn = parseSheetPriceInputToNumber(priceRaw);
            if (pn != null) initialPatch.price = formatSheetRowPriceInputValue(pn);
          }
        }
        initialPatch.unit = modUnit.trim();
        initialPatch.unquoted = effectiveUnquoted;
        initialPatch.valueAddedTax = effectiveUnquoted ? false : modVat;
        const res = await onAddItem({
          kind: 'freeform',
          userCategoryId: cid,
          rawName: name,
          initialPatch,
        });
        if (!res.ok) {
          setAddModalError((res.message ?? '').trim() || 'No se pudo registrar el material.');
          return;
        }
        setFeedbackToast({ variant: 'success', text: 'Material guardado en tu catálogo.' });
        setAddOpen(false);
        resetAddModalForm();
      } catch {
        setAddModalError('No se pudo registrar el material.');
      } finally {
        setAddModalSubmitting(false);
      }
    })();
  }, [
    catalogMaterialPick,
    modCategoryId,
    modDesc,
    modDisc,
    modPrice,
    modPromo,
    modRawName,
    modUnquoted,
    modUnit,
    modVat,
    onAddItem,
    resetAddModalForm,
  ]);

  useEffect(() => {
    if (!addOpen || !onAddItem) return;
    let cancelled = false;
    void (async () => {
      try {
        const cats = await fetchCatalogCategories();
        if (cancelled) return;
        setAddModalCategoryOptions(
          cats.map((c) => ({ value: String(c.id), label: c.name })).sort((a, b) => a.label.localeCompare(b.label, 'es'))
        );
      } catch {
        if (cancelled) return;
        setAddModalCategoryOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addOpen, onAddItem]);

  useEffect(() => {
    if (!addOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (addModalSubmitting) return;
        setAddOpen(false);
        resetAddModalForm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addOpen, addModalSubmitting, resetAddModalForm]);
  const startEdit = (row: FlatMaterialRow) => {
    const mc = row.material.commerceRow;
    if (!mc || mc.itemId == null) return;
    setEditingRow(row);
    setEditItemId(mc.itemId);
  };

  const confirmDeleteMaterialItem = useCallback(
    (productName: string, itemId: number) => {
      if (!onDeleteItem) return;
      const safeName = productName.trim() || 'este producto';
      dispatch(
        openMainModal({
          title: '¿Eliminar este producto?',
          body: `Vas a sacar "${safeName}" del listado de tu comercio. Podés volver a cargarlo más adelante.`,
          iconType: 'warning',
          isSuccess: false,
          yesLabel: 'Eliminar',
          noLabel: 'Cancelar',
          confirmHandler: () => {
            dispatch(closeMainModal());
            void (async () => {
              try {
                await onDeleteItem(itemId);
              } catch (e: unknown) {
                const msg =
                  (isAxiosError(e) && typeof e.response?.data === 'object' && e.response?.data != null
                    ? (e.response.data as { message?: string }).message
                    : null) ||
                  (e instanceof Error ? e.message : null) ||
                  'No pudimos eliminar el producto.';
                dispatch(
                  openMainModal({
                    title: 'No se pudo eliminar',
                    body: typeof msg === 'string' ? msg : 'No pudimos eliminar el producto.',
                    iconType: 'error',
                    isSuccess: true,
                    confirmHandler: null,
                  })
                );
              }
            })();
          },
        })
      );
    },
    [dispatch, onDeleteItem]
  );

  const cancelEdit = useCallback(() => {
    setEditingRow(null);
    setEditItemId(null);
  }, []);

  const closeAddModal = useCallback(() => {
    setAddOpen(false);
    resetAddModalForm();
  }, [resetAddModalForm]);

  if (flatRows.length === 0) {
    return <p className={styles.empty}>No hay materiales para mostrar.</p>;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <div className={styles.fieldGrow}>
          <label className={styles.label} htmlFor="cmc-search">
            Buscar
          </label>
          <input
            id="cmc-search"
            className={styles.input}
            type="search"
            autoComplete="off"
            placeholder="Nombre, marca, categoría…"
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
          />
        </div>
        <div className={styles.fieldFixed}>
          <label className={styles.label} htmlFor="cmc-cat">
            Categoría
          </label>
          <select
            id="cmc-cat"
            className={styles.select}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value === 'all' ? 'all' : e.target.value)}
          >
            <option value="all">Todas ({flatRows.length})</option>
            {categoryOptions.map(([id, name]) => {
              const count = flatRows.filter((r) => r.categoryId === id).length;
              return (
                <option key={id} value={id}>
                  {name} ({count})
                </option>
              );
            })}
          </select>
        </div>
        {ownerCatalogToolbarEnabled ? (
          <div className={styles.toolbarAdd}>
            <div className={styles.toolbarAddRow}>
              {fileImportHref ? (
                <button
                  type="button"
                  className={styles.addToolbarBtn}
                  disabled={disableActions}
                  onClick={() => navigate(fileImportHref)}
                >
                  Cargar o agregar materiales
                </button>
              ) : null}
              {onAddItem && !fileImportHref ? (
                <button
                  type="button"
                  className={styles.addToolbarBtn}
                  disabled={disableActions}
                  onClick={() => {
                    resetAddModalForm();
                    setAddOpen(true);
                  }}
                >
                  Agregar material
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <p className={styles.meta}>
        {filtered.length === flatRows.length
          ? `${flatRows.length} materiales`
          : `${filtered.length} de ${flatRows.length} materiales`}
        {filtered.length > 0 ? ` · filas ${fromN}–${toN}` : null}
      </p>
      {filtered.length === 0 ? (
        <p className={styles.empty}>No hay resultados con los filtros actuales.</p>
      ) : (
        <>
          <div
            ref={catalogTableDragScroll.ref}
            className={styles.tableWrap}
            onMouseDown={catalogTableDragScroll.onMouseDown}
            onMouseMove={catalogTableDragScroll.onMouseMove}
            onMouseUp={catalogTableDragScroll.onMouseUp}
            onMouseLeave={catalogTableDragScroll.onMouseLeave}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Categoría</th>
                  <th className={styles.th}>Material</th>
                  <th className={styles.th}>Marca</th>
                  <th className={styles.th}>Unidad</th>
                  {mode === 'owner' ? (
                    <>
                      <th className={styles.th}>Precio estimado</th>
                      <th className={styles.th}>Tu precio</th>
                      <th className={styles.th}>Promoción</th>
                      <th className={styles.th}>IVA</th>
                      {ownerActionsEnabled ? <th className={styles.th}>Acciones</th> : null}
                    </>
                  ) : (
                    <th className={styles.th}>Precio</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const m = row.material;
                  const mc = m.commerceRow;
                  const needsQuote = quoteFlag(m);
                  return (
                    <tr key={`${row.categoryId}-${m.id}`}>
                      <td className={styles.td}>{row.categoryName}</td>
                      <td className={styles.td}>
                        {mode === 'public' && onPublicMaterialDetailClick ? (
                          <button
                            type="button"
                            className={styles.materialDetailLink}
                            title="Ver ficha del material"
                            onClick={() => onPublicMaterialDetailClick(m.id)}
                          >
                            <span className={styles.cellMain}>{m.name}</span>
                          </button>
                        ) : (
                          <span className={styles.cellMain}>{m.name}</span>
                        )}
                        {mode === 'owner' && m.description ? (
                          <span className={`${styles.cellSub} ${styles.cellEllipsis}`}>{m.description}</span>
                        ) : null}
                      </td>
                      <td className={styles.td}>{m.brand ?? '—'}</td>
                      <td className={styles.td}>{m.unit ?? '—'}</td>
                      {mode === 'owner' ? (
                        <>
                          <td className={styles.tdNum}>{formatMoneyArs(m.catalogPrice)}</td>
                          <td className={`${styles.td} ${styles.priceCol}`}>
                            <div className={styles.priceCell}>
                              <span className={styles.priceValue}>{formatMoneyArs(mc?.price ?? null)}</span>
                              <span className={styles.pricePublicHint}>
                                {mc
                                  ? needsQuote
                                    ? 'Precio a consultar (no visible al público)'
                                    : 'Precio visible al público'
                                  : 'Todavía no cargaste precio para este comercio'}
                              </span>
                            </div>
                          </td>
                          <td className={styles.td}>
                            <span className={styles.cellEllipsis}>{promotionLabel(mc)}</span>
                          </td>
                          <td className={styles.td}>
                            {mc?.value_added_tax === true
                              ? 'Sí'
                              : mc?.value_added_tax === false
                                ? 'No'
                                : '—'}
                          </td>
                          {ownerActionsEnabled ? (
                            <td className={styles.td}>
                              {mc?.itemId != null ? (
                                <div className={styles.rowActions}>
                                  <div className={styles.actionBtns}>
                                    <button
                                      type="button"
                                      className={styles.actionBtn}
                                      disabled={disableActions}
                                      onClick={() => startEdit(row)}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      className={styles.actionBtnDanger}
                                      disabled={disableActions}
                                      onClick={() => {
                                        if (mc.itemId == null) return;
                                        confirmDeleteMaterialItem(m.name, mc.itemId);
                                      }}
                                    >
                                      Eliminar
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span className={styles.cellSub}>Sin itemId</span>
                              )}
                            </td>
                          ) : null}
                        </>
                      ) : (
                        <td className={styles.td}>
                          {needsQuote ? (
                            <span className={styles.badge}>Precio a consultar</span>
                          ) : mc?.price != null && Number.isFinite(mc.price) ? (
                            <div className={styles.priceCell}>
                              <span className={styles.priceValue}>{formatMoneyArs(mc.price)}</span>
                              <span className={styles.pricePublicHint}>
                                Importe publicado en este comercio
                              </span>
                            </div>
                          ) : (
                            <span className={styles.badgeMuted}>Publicado sin importe visible</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 ? (
            <footer className={styles.pager} aria-label="Paginación del catálogo">
              <button type="button" disabled={page <= 1} onClick={() => onCatalogPageNav('prev')}>
                Anterior
              </button>
              <span>
                Página {page} de {pageCount}
              </span>
              <button type="button" disabled={page >= pageCount} onClick={() => onCatalogPageNav('next')}>
                Siguiente
              </button>
            </footer>
          ) : null}
        </>
      )}
      {ownerManualAddEnabled && onAddItem ? (
        <CommerceSheetManualAddMaterialModal
          addModalOpen={addOpen}
          closeAddMaterialModal={closeAddModal}
          addModalSubmitting={addModalSubmitting}
          submitAddMaterialModal={submitAddMaterialModal}
          addModalError={addModalError}
          globalCatalogFetchError={globalCatalogFetchError}
          categorySelectOptions={addModalCategoryOptions}
          modCategoryId={modCategoryId}
          onModCategoryChange={onModCategoryChange}
          modRawName={modRawName}
          setModRawName={setModRawName}
          catalogMaterialPick={catalogMaterialPick}
          setCatalogMaterialPick={setCatalogMaterialPick}
          setAddModalError={setAddModalError}
          globalCatalogStatus={globalCatalogStatus}
          globalCatalogMatches={globalCatalogMatches}
          modDesc={modDesc}
          setModDesc={setModDesc}
          modPromo={modPromo}
          setModPromo={setModPromo}
          modDisc={modDisc}
          setModDisc={setModDisc}
          modPrice={modPrice}
          setModPrice={setModPrice}
          modUnit={modUnit}
          setModUnit={setModUnit}
          modUnquoted={modUnquoted}
          setModUnquoted={setModUnquoted}
          modVat={modVat}
          setModVat={setModVat}
          setModCategoryId={setModCategoryId}
          overlayZIndex={80}
          fieldErrors={addModalFieldErrors}
          saveButtonDisabled={manualAddSaveDisabled}
        />
      ) : null}
      {onEditItem ? (
        <CommerceMaterialsCatalogEditModal
          isOpen={editingRow != null && editItemId != null}
          row={editingRow}
          itemId={editItemId}
          flatRows={flatRows}
          disableActions={disableActions}
          onClose={cancelEdit}
          onSave={editItemWithFeedback}
        />
      ) : null}
      {feedbackToast ? (
        <div
          role="status"
          className={`${styles.catalogToast} ${
            feedbackToast.variant === 'success' ? styles.catalogToastSuccess : styles.catalogToastError
          }`}
        >
          {feedbackToast.text}
        </div>
      ) : null}
    </div>
  );
};

export default CommerceMaterialsCatalog;
